/**
 * Job Candidates Page — matched second shooters
 * Layout: info top, scrollable portfolio strip bottom
 * Filters: insured, has portfolio, in rate range
 * Sort: match, rating, rate
 */
import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Star, Heart, MessageSquare, MapPin, DollarSign,
  Shield, Loader2, Lock, Tag, Users, Camera, Car, ChevronDown,
  Globe, Instagram, ChevronLeft, ChevronRight, FileText, UserCheck, Calendar, Send, Plane,
} from "lucide-react";
import { GENRES, HIREABLE_ROLES } from "@/lib/profileOptions";

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export default function JobCandidatesPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [autoFree, setAutoFree] = useState(false);
  const [candidatesHidden, setCandidatesHidden] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);

  const [sortBy, setSortBy] = useState("match_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filterInsurance, setFilterInsurance] = useState(false);
  const [filterPortfolio, setFilterPortfolio] = useState(false);
  const [filterInRate, setFilterInRate] = useState(true);
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filterMinRating, setFilterMinRating] = useState(0);
  const [filterLocalOnly, setFilterLocalOnly] = useState(true);
  
  // Hire dialog state
  const [hireDialogOpen, setHireDialogOpen] = useState(false);
  const [hireCandidate, setHireCandidate] = useState(null);
  const [hireMessage, setHireMessage] = useState("");
  const [finalRate, setFinalRate] = useState("");
  const [rateType, setRateType] = useState("hourly");
  const [hiring, setHiring] = useState(false);
  
  // Contract state
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [existingContract, setExistingContract] = useState(null);

  // Hire info state
  const [hireInfo, setHireInfo] = useState(null);

  useEffect(() => {
    if (token) fetchJobAndCandidates();
  }, [jobId, token]);

  const fetchJobAndCandidates = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [jobRes, candRes, hireRes] = await Promise.all([
        fetch(`${API}/jobs/${jobId}`, { headers }),
        fetch(`${API}/jobs/${jobId}/candidates`, { headers }),
        fetch(`${API}/jobs/${jobId}/hire-info`, { headers }),
      ]);
      if (jobRes.ok) setJob(await jobRes.json());
      if (candRes.ok) {
        const data = await candRes.json();
        setRequiresPayment(data.requires_payment);
        setCandidates(data.candidates || []);
        setAutoFree(data.auto_free || false);
        setCandidatesHidden(data.hidden || false);
      }
      if (hireRes.ok) {
        const data = await hireRes.json();
        if (data) setHireInfo(data);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Handle per-job checkout - directly go to Stripe
  const handlePerJobCheckout = async () => {
    setCheckingOut(true);
    try {
      const params = new URLSearchParams({
        plan_type: "studio_per_job",
        job_id: jobId,
        origin_url: window.location.origin
      });
      
      const response = await fetch(`${API}/subscriptions/checkout?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start checkout");
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setCheckingOut(false);
    }
  };

  const handleFavorite = async (userId, isFav) => {
    try {
      await fetch(`${API}/favorites/${userId}`, {
        method: isFav ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates((p) =>
        p.map((c) => (c.user_id === userId ? { ...c, is_favorited: !isFav } : c))
      );
    } catch {}
  };

  const handleHire = async (candidateId, candidateName) => {
    const candidate = candidates.find(c => c.user_id === candidateId);
    setHireCandidate({ ...candidate, user_id: candidateId, name: candidateName });
    setHireMessage("");
    setFinalRate("");
    setRateType(job?.payment_type || (job?.hourly_rate ? "hourly" : job?.flat_rate ? "flat" : "hourly"));
    setSelectedTemplate(job?.contract_template_id || "");
    setExistingContract(null);
    setHireDialogOpen(true);

    // Fetch contract templates and check existing contracts
    try {
      const [tmplRes, checkRes] = await Promise.all([
        fetch(`${API}/contracts/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/contracts/check/${candidateId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (tmplRes.ok) {
        const data = await tmplRes.json();
        setContractTemplates(data.templates);
        // Pre-select the job's contract if templates loaded and job has one
        if (job?.contract_template_id && data.templates.some(t => t.template_id === job.contract_template_id)) {
          setSelectedTemplate(job.contract_template_id);
        }
      }
      if (checkRes.ok) {
        const data = await checkRes.json();
        if (data.has_contract) setExistingContract(data.contract);
      }
    } catch {}
  };

  const confirmHire = async () => {
    if (!hireCandidate) return;
    
    setHiring(true);
    try {
      // Step 1: Create contract if template selected
      if (selectedTemplate) {
        const contractParams = new URLSearchParams({
          job_id: jobId,
          candidate_id: hireCandidate.user_id,
          template_id: selectedTemplate,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        if (finalRate.trim()) contractParams.set("final_rate", finalRate.trim());
        const cRes = await fetch(
          `${API}/contracts/create-for-hire?${contractParams.toString()}`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cRes.ok) {
          const err = await cRes.json();
          toast.error(err.detail || "Failed to create contract");
          setHiring(false);
          return;
        }
      }

      // Step 2: Hire the candidate
      const res = await fetch(`${API}/jobs/${jobId}/hire/${hireCandidate.user_id}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          message: hireMessage.trim() || null,
          final_rate: finalRate.trim() || null,
          rate_type: rateType
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Successfully hired ${hireCandidate.name}!`);
        setHireDialogOpen(false);
        navigate(`/jobs/${jobId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to hire candidate");
      }
    } catch (error) {
      toast.error("Network error - please try again");
    } finally {
      setHiring(false);
    }
  };

  const handleApplyCode = async () => {
    if (!discountCode.trim()) return;
    setApplyingCode(true);
    setCodeError("");
    try {
      const valRes = await fetch(
        `${API}/admin/discount-codes/validate?code=${encodeURIComponent(discountCode)}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const valData = await valRes.json();
      if (!valData.valid) {
        setCodeError(valData.error || "Invalid code");
        setApplyingCode(false);
        return;
      }
      const redeemRes = await fetch(
        `${API}/admin/discount-codes/redeem?code=${encodeURIComponent(discountCode)}&job_id=${jobId}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (redeemRes.ok) {
        toast.success("Code applied!");
        setDiscountCode("");
        setCodeError("");
        fetchJobAndCandidates();
      } else {
        let msg = "Failed to apply code";
        try { msg = (await redeemRes.json()).detail || msg; } catch {}
        setCodeError(msg);
      }
    } catch (e) {
      setCodeError("Network error — please try again");
    } finally {
      setApplyingCode(false);
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortBy(field); setSortDir("desc"); }
  };

  // Determine job rate range for "in rate" filter.
  // Prefer explicit rate_min/rate_max (flexible jobs), else fall back to hourly_rate or flat_rate.
  const jobRateMin = job?.rate_min ?? job?.hourly_rate ?? job?.flat_rate ?? null;
  const jobRateMax = job?.rate_max ?? job?.hourly_rate ?? job?.flat_rate ?? null;

  const filtered = candidates
    .filter((c) => !filterFavorites || c.is_favorited)
    .filter((c) => !filterInsurance || c.has_insurance)
    .filter((c) => !filterPortfolio || (c.portfolio_images?.length > 0) || (c.portfolio_videos?.length > 0))
    .filter((c) => filterMinRating === 0 || (c.rating || 0) >= filterMinRating)
    .filter((c) => {
      if (!filterLocalOnly) return true;
      // Local only: exclude candidates who are outside range and only included because willing_to_travel
      if (c.is_willing_to_travel && c.distance_miles && c.distance_miles > (c.location?.work_radius || 50)) return false;
      return true;
    })
    .filter((c) => {
      if (!filterInRate || jobRateMin == null) return true;
      // Candidate rate (may be null/undefined if they haven't set one)
      const cMinRaw = c.hourly_rate ?? c.rate_min;
      const cMaxRaw = c.rate_max ?? cMinRaw;
      // If the candidate hasn't listed a rate for this role, INCLUDE them
      // (they haven't limited themselves — the studio can negotiate).
      if (cMinRaw == null && cMaxRaw == null) return true;
      const cMin = cMinRaw ?? cMaxRaw;
      const cMax = cMaxRaw ?? cMinRaw;
      // Include if candidate's rate range OVERLAPS with job's rate ±20% tolerance.
      // Exclude only when candidate's entire range is OUTSIDE the ±20% window.
      const jobMax = jobRateMax ?? jobRateMin;
      return cMin <= jobMax * 1.2 && cMax >= jobRateMin * 0.8;
    })
    .sort((a, b) => {
      // Always float contacted candidates to the top so studios can see follow-ups first
      if (!!a.contacted !== !!b.contacted) return a.contacted ? -1 : 1;
      const dir = sortDir === "desc" ? -1 : 1;
      return ((a[sortBy] ?? 0) - (b[sortBy] ?? 0)) * dir;
    });

  const roleName = HIREABLE_ROLES.find((r) => r.value === job?.role_needed)?.label || job?.role_needed;
  const genreName = GENRES.find((g) => g.value === job?.genre)?.label || job?.genre;

  const getPayDisplay = () => {
    if (!job) return "";
    if (job.is_flexible) return job.rate_min && job.rate_max ? `$${job.rate_min}–$${job.rate_max}` : "Flexible";
    return job.payment_type === "hourly" ? `$${job.hourly_rate}/hr` : `$${job.flat_rate}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">Dashboard</Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-16 px-4 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">My Jobs</span>
          </Link>

          {/* Job Header */}
          {job && (
            <div className="mb-8" data-testid="job-summary">
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{job.title}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-white/60 text-sm">
                <span>{roleName}</span>
                <span>{genreName}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.venue_name && `${job.venue_name}, `}{job.location_info?.city || job.location?.city}{(job.location_info?.state || job.location?.state) && `, ${job.location_info?.state || job.location?.state}`}</span>
                <span>{(() => { if (!job.date) return "TBD"; const p = job.date.split("T")[0].split("-"); return new Date(+p[0], +p[1]-1, +p[2]).toLocaleDateString(); })()}</span>
                <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{getPayDisplay()}</span>
              </div>
            </div>
          )}

          {/* Payment Wall */}
          {requiresPayment && (
            <Card className="p-8 text-center" data-testid="payment-wall">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white/20" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Publish this job</h2>
              {/* Was "Unlock Candidates", which read as an optional upgrade for
                  browsing matches. It is not optional: until this is paid the
                  listing is not in search, nobody is emailed about it, and no
                  one can apply. Studios were skipping it and waiting for
                  applicants that could never arrive. */}
              <p className="text-white/50 mb-2 max-w-md mx-auto">
                Your job is saved but <span className="text-white/80">not yet visible</span>.
                Publishing puts it in search, emails second shooters nearby, and lets them apply.
              </p>
              <p className="text-white/50 mb-6 max-w-md mx-auto">
                $10 for this job, or $19/mo for unlimited posts. You will also see your matched
                candidates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  onClick={handlePerJobCheckout}
                  disabled={checkingOut}
                  className="bg-white text-black hover:bg-gray-200 font-bold px-8"
                  data-testid="pay-per-job-btn"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "$10 — This Job"
                  )}
                </Button>
                <Link to="/pricing?type=studio">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8">$19/mo — Unlimited</Button>
                </Link>
              </div>
              <div className="border-t border-white/5 pt-6 max-w-sm mx-auto">
                <div className="flex items-center gap-2 text-white/30 text-sm mb-3 justify-center"><Tag className="w-4 h-4" />Have a code?</div>
                <div className="flex gap-2">
                  <Input value={discountCode} onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setCodeError(""); }} placeholder="Enter code" className={`bg-white/[0.05] border-white/10 text-white h-10 uppercase font-mono ${codeError ? "border-red-500/60" : ""}`} data-testid="discount-code-input" />
                  <Button onClick={handleApplyCode} disabled={applyingCode || !discountCode.trim()} className="bg-white text-black hover:bg-gray-200 h-10 px-6 font-bold shrink-0" data-testid="apply-code-btn">
                    {applyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                {codeError && (
                  <p className="text-red-400 text-sm mt-2 text-center" data-testid="code-error-message">{codeError}</p>
                )}
              </div>
            </Card>
          )}

          {/* Candidates Hidden for completed/expired jobs */}
          {candidatesHidden && (
            <Card className="p-8 text-center" data-testid="candidates-hidden">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white/20" />
              </div>
              <h2 className="text-xl font-bold mb-2">Candidates No Longer Available</h2>
              <p className="text-white/50 mb-6 max-w-md mx-auto">
                The candidate list is no longer available for completed or expired jobs.
              </p>
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
              </Link>
            </Card>
          )}

          {/* Candidates List */}
          {!requiresPayment && !candidatesHidden && (
            <>
              {/* Sort & Filter */}
              <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="sort-filter-bar">
                <span className="text-white/50 text-sm font-medium">{filtered.length} match{filtered.length !== 1 ? "es" : ""}</span>
                <SortBtn label="Best Match" active={sortBy === "match_score"} dir={sortBy === "match_score" ? sortDir : null} onClick={() => toggleSort("match_score")} />
                <SortBtn label="Rating" active={sortBy === "rating"} dir={sortBy === "rating" ? sortDir : null} onClick={() => toggleSort("rating")} />
                <SortBtn label="Rate" active={sortBy === "hourly_rate"} dir={sortBy === "hourly_rate" ? sortDir : null} onClick={() => toggleSort("hourly_rate")} />
                <div className="flex flex-wrap gap-3 ml-auto text-sm">
                  <FilterCheck label="Local Only" checked={filterLocalOnly} onChange={setFilterLocalOnly} icon={<MapPin className="w-3 h-3" />} />
                  <FilterCheck label="Favorites Only" checked={filterFavorites} onChange={setFilterFavorites} icon={<Heart className="w-3 h-3" />} />
                  <FilterCheck label="Has Portfolio" checked={filterPortfolio} onChange={setFilterPortfolio} />
                  <FilterCheck label="In Rate Range" checked={filterInRate} onChange={setFilterInRate} />
                  <FilterCheck label="Insured" checked={filterInsurance} onChange={setFilterInsurance} />
                  <div className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-yellow-400" />
                    <select
                      value={filterMinRating}
                      onChange={(e) => setFilterMinRating(Number(e.target.value))}
                      className="bg-white/[0.05] border border-white/10 rounded text-sm text-white py-0.5 px-2 focus:outline-none focus:border-white/30"
                      data-testid="filter-min-rating"
                    >
                      <option value={0}>Any Rating</option>
                      <option value={3}>3+ Stars</option>
                      <option value={4}>4+ Stars</option>
                      <option value={4.5}>4.5+ Stars</option>
                    </select>
                  </div>
                </div>
              </div>

              {filtered.length === 0 ? (
                <Card className="p-8 text-center" data-testid="no-candidates-card">
                  <Users className="w-10 h-10 text-white/15 mx-auto mb-4" />
                  {autoFree ? (
                    <>
                      <h3 className="text-lg font-bold mb-2">No Candidates Available Yet</h3>
                      <p className="text-white/50">This listing is <span className="text-[#3498DB] font-semibold">free</span>. We'll email you when someone matches.</p>
                    </>
                  ) : (
                    <p className="text-white/60">No candidates match your filters.</p>
                  )}
                </Card>
              ) : (
                <div className="space-y-5" data-testid="candidates-list">
                  {filtered.map((c) => (
                    <CandidateCard key={c.user_id} c={c} hireInfo={hireInfo} onFav={handleFavorite} onMsg={(id) => navigate(`/messages?to=${id}&job=${jobId}`)} onProfile={(id) => navigate(`/profile/${id}?job_id=${jobId}`)} onHire={handleHire} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Hire Dialog */}
      <Dialog open={hireDialogOpen} onOpenChange={setHireDialogOpen}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Hire {hireCandidate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-white/70 text-sm">
              This will mark the job as filled and notify the candidate.
            </p>
            
            {/* Contract Selection */}
            {contractTemplates.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Attach Contract
                </label>
                {existingContract && (
                  <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-white/10 border border-white/20 rounded text-xs text-white/70">
                    <FileText className="w-3.5 h-3.5" />
                    Existing per-person contract on file (signed {new Date(existingContract.signed_at).toLocaleDateString()})
                  </div>
                )}
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="contract-template-select"
                >
                  <option value="">No contract</option>
                  {contractTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.title} ({t.contract_type === "per_job" ? "Per Job" : "Per Person"})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-white/30 mt-1">
                  The candidate will be asked to sign before the job begins.
                </p>
              </div>
            )}

            {/* Final Rate */}
            <div>
              <label className="block text-sm font-medium mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Final Agreed Rate
              </label>
              <div className="flex gap-2">
                <select value={rateType} onChange={(e) => setRateType(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm" data-testid="rate-type-select">
                  <option value="hourly">Per Hour</option>
                  <option value="flat">Per Job (Flat)</option>
                </select>
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input
                    value={finalRate}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setFinalRate(v); }}
                    placeholder={job?.hourly_rate ? String(job.hourly_rate) : job?.flat_rate ? String(job.flat_rate) : "0"}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7"
                    data-testid="final-rate-input"
                    inputMode="decimal"
                  />
                </div>
              </div>
              <p className="text-xs text-white/30 mt-1">
                Enter the numeric rate. This will be used in the contract and offer.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Message to candidate (optional)
              </label>
              <Textarea
                value={hireMessage}
                onChange={(e) => setHireMessage(e.target.value)}
                placeholder="Welcome to the team! Looking forward to working with you..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[100px]"
                maxLength={500}
              />
              <div className="text-xs text-white/40 mt-1">
                {hireMessage.length}/500 characters
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setHireDialogOpen(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                disabled={hiring}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmHire}
                disabled={hiring}
                className="flex-1 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold"
                data-testid="confirm-hire-btn"
              >
                {hiring ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Hiring...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Confirm Hire
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

function SortBtn({ label, active, dir, onClick }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all flex items-center gap-1 ${active ? "border-white/30 bg-white/10 text-white" : "border-white/10 text-white/40 hover:text-white/60 hover:border-white/20"}`} data-testid={`sort-${label.toLowerCase().replace(" ", "-")}`}>
      {label}
      {active && <ChevronDown className={`w-3 h-3 transition-transform ${dir === "asc" ? "rotate-180" : ""}`} />}
    </button>
  );
}

function FilterCheck({ label, checked, onChange, icon }) {
  return (
    <label className="flex items-center gap-1.5 text-white/50 cursor-pointer hover:text-white/70 transition-colors" data-testid={`filter-${label.toLowerCase().replace(/ /g, "-")}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#FF6B6B] w-3.5 h-3.5" data-testid={`filter-${label.toLowerCase().replace(/ /g, "-")}-checkbox`} />
      {icon && <span className={checked ? "text-[#FF6B6B]" : ""}>{icon}</span>}
      <span className="text-xs">{label}</span>
    </label>
  );
}

function CandidateCard({ c, hireInfo, onFav, onMsg, onProfile, onHire }) {
  const scrollRef = useRef(null);
  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const isHired = hireInfo && hireInfo.candidate_id === c.user_id;
  const hirePending = isHired && hireInfo.status === "pending_confirmation";
  const hireConfirmed = isHired && (hireInfo.status === "confirmed" || hireInfo.status === "hired");

  return (
    <Card className="overflow-hidden" style={{ border: isHired ? "1px solid rgba(255,107,107,0.4)" : "1px solid rgba(255,255,255,0.18)", background: isHired ? "rgba(255,107,107,0.03)" : undefined }} data-testid={`candidate-${c.user_id}`}>
      {/* Hire status banner */}
      {hirePending && (
        <div className="px-5 py-2.5 bg-amber-500/15 border-b border-amber-500/20 flex items-center gap-2" data-testid={`candidate-hire-pending-${c.user_id}`}>
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <span className="text-sm font-semibold text-amber-400">Hire Offer Sent — Awaiting Response</span>
        </div>
      )}
      {hireConfirmed && (
        <div className="px-5 py-2.5 bg-green-500/15 border-b border-green-500/20 flex items-center gap-2" data-testid={`candidate-hire-confirmed-${c.user_id}`}>
          <UserCheck className="w-4 h-4 text-green-400" />
          <span className="text-sm font-semibold text-green-400">Hired</span>
        </div>
      )}
      {/* Top: info */}
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              {c.profile_image ? (
                <img src={c.profile_image} alt={c.name} className="w-11 h-11 rounded-full object-cover border border-white/15" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white/40 text-sm font-bold">{c.name?.charAt(0)}</div>
              )}
              {/* PRO badge on avatar */}
              {c.is_pro && (
                <span className="absolute -bottom-1 -right-1 bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-[8px] font-black px-1.5 py-0.5 rounded-full shadow-lg">
                  PRO
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-base text-white">{c.name}</h3>
                {/* PRO badge next to name */}
                {c.is_pro && (
                  <span className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-[9px] font-black px-2 py-0.5 rounded-full">
                    PRO
                  </span>
                )}
                {c.contacted && (
                  <span
                    className="inline-flex items-center gap-1 bg-[#3498DB]/15 text-[#3498DB] border border-[#3498DB]/30 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    title={c.contacted_at ? `Last message sent ${new Date(c.contacted_at).toLocaleDateString()}` : "Already contacted"}
                    data-testid={`contacted-badge-${c.user_id}`}
                  >
                    <MessageSquare className="w-2.5 h-2.5" />
                    Contacted
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-white/50 text-xs mt-0.5">
                {c.location?.city && (
                  <span className="flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {c.location.city}{c.location.state ? `, ${c.location.state}` : ""}
                  </span>
                )}
                {c.years_experience && <span>{c.years_experience}yr exp</span>}
                {c.rating && (
                  <span className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-[#FF6B6B] text-[#FF6B6B]" />
                    {c.rating.toFixed(1)} <span className="text-white/30">({c.review_count})</span>
                  </span>
                )}
                {c.last_active && (
                  <span className="text-white/30" data-testid={`last-active-${c.user_id}`}>
                    Active {formatTimeAgo(c.last_active)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider shrink-0 ${c.match_score >= 80 ? "bg-[rgba(255,107,107,0.15)] text-[#FF6B6B]" : c.match_score >= 60 ? "bg-white/10 text-white/70" : "bg-white/5 text-white/40"}`}>
            {c.match_score}% match
          </span>
        </div>

        {/* Rate + badges row */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm font-bold text-white">
            ${c.hourly_rate}{c.rate_max && c.rate_max !== c.hourly_rate ? `–$${c.rate_max}` : ""}
            <span className="text-white/40 font-normal text-xs ml-0.5">/hr</span>
          </span>
          {/* Calendar availability badge - only show "Available" for connected calendars */}
          {c.calendar_connected && c.calendar_available && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-white/10 text-white/70 border border-white/20">
              <Calendar className="w-3 h-3" />
              Available
            </span>
          )}
          {/* Willing to Travel badge - show when candidate is outside work radius but willing to travel */}
          {c.is_willing_to_travel && c.distance_miles && c.distance_miles > (c.location?.work_radius || 50) && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-coral/10 text-coral border border-coral/20">
              <Plane className="w-3 h-3" />
              Will Travel
            </span>
          )}
          {c.has_insurance && <Badge icon={<Shield className="w-3 h-3" />} label="Insured" />}
          {c.has_transportation && <Badge icon={<Car className="w-3 h-3" />} label="Transport" />}
          {c.has_backup_gear && <Badge icon={<Camera className="w-3 h-3" />} label="Backup Gear" />}
          {c.w9_uploaded && <Badge icon={<FileText className="w-3 h-3" />} label="W9 on file" color="coral" />}
        </div>

        {/* Styles */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {c.shooting_styles?.map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-white/50">{s}</span>
          ))}
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => onProfile(c.user_id)} className="bg-white text-black hover:bg-gray-200 text-xs font-bold px-4" data-testid={`view-profile-${c.user_id}`}>View Profile</Button>
          <Button size="sm" variant="outline" onClick={() => onMsg(c.user_id)} className="border-white/15 text-white/70 hover:bg-white/5 text-xs" data-testid={`message-${c.user_id}`}>
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
          </Button>
          {!isHired && (
            <Button size="sm" onClick={() => onHire(c.user_id, c.name)} className="border-white/15 text-white/70 hover:bg-white/5 text-xs" variant="outline" data-testid={`hire-${c.user_id}`}>
              Hire
            </Button>
          )}
          {c.website_url && (
            <a href={c.website_url} target="_blank" rel="noopener noreferrer" className="p-2 text-white/30 hover:text-white/60 transition-colors" title="Website">
              <Globe className="w-4 h-4" />
            </a>
          )}
          {c.instagram_handle && (
            <a href={`https://instagram.com/${c.instagram_handle.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="p-2 text-white/30 hover:text-white/60 transition-colors" title={c.instagram_handle}>
              <Instagram className="w-4 h-4" />
            </a>
          )}
          <button onClick={() => onFav(c.user_id, c.is_favorited)} className={`ml-auto p-2 rounded-full transition-colors ${c.is_favorited ? "text-[#FF6B6B] bg-[rgba(255,107,107,0.1)]" : "text-white/20 hover:text-white/40"}`} data-testid={`fav-${c.user_id}`}>
            <Heart className={`w-4 h-4 ${c.is_favorited ? "fill-current" : ""}`} />
          </button>
        </div>
      </div>

      {/* Bottom: scrollable portfolio strip */}
      {c.portfolio_images?.length > 0 && (
        <div className="relative border-t border-white/10 bg-black/20 group">
          <div ref={scrollRef} className="flex overflow-x-auto scrollbar-hide gap-0.5 scroll-smooth" style={{ scrollbarWidth: "none" }}>
            {c.portfolio_images.map((img, i) => (
              <img key={i} src={img} alt={`${c.name} work ${i + 1}`} className="h-28 md:h-36 w-auto object-cover shrink-0 hover:brightness-110 transition-all cursor-pointer" loading="lazy" />
            ))}
          </div>
          {c.portfolio_images.length > 2 && (
            <>
              <button onClick={() => scroll(-1)} className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 text-white/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => scroll(1)} className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 text-white/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Badge({ icon, label, color }) {
  const colorClass = color === "coral" 
    ? "text-coral bg-coral/10" 
    : "text-white/50 bg-white/5";
  return (
    <span className={`flex items-center gap-1 text-[10px] ${colorClass} px-2 py-0.5 rounded-full`}>
      {icon} {label}
    </span>
  );
}
